import React from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/organisms/Navigation';
import Footer from '../components/organisms/Footer';
import RevealText from '../components/atoms/RevealText';
import VideoModal from '../components/organisms/VideoModal';
import PageTransition from '../components/templates/PageTransition';
import SEO from '../components/utils/SEO';

const About = () => {
    const [videoModalOpen, setVideoModalOpen] = React.useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const PHILOSOPHY = [
        {
            title: "The approach",
            desc: "I believe in digital craftsmanship. Every line of code, every pixel, every interaction is a deliberate choice. It's not just about making things work; it's about making them matter."
        },
        {
            title: "The journey",
            desc: "From tinkering with early web standards to building complex applications, my journey has been defined by a relentless pursuit of better. Better performance, better design, better experiences."
        },
        {
            title: "The toolkit",
            desc: "My weapon of choice is the modern web stack. React, Node.js, and a deep understanding of browser internals allow me to build things that feel like magic, not just software."
        }
    ];

    return (
        <PageTransition>
            <SEO
                title="About"
                description="Digital craftsman based in Indonesia. I prioritize control, performance, and microscopic details."
            />
            <div className="bg-[#050505] text-[#fbfbf4] font-sans selection:bg-[#fbfbf4] selection:text-[#050505] overflow-x-hidden">
                <Navigation onVideoClick={() => setVideoModalOpen(true)} />

                <main className="relative z-10 pt-32 pb-20">
                    {/* HERO */}
                    <section className="px-4 md:px-8 mb-20 border-b border-white/10 pb-8 flex justify-between items-end">
                        <span className="text-xs uppercase tracking-[0.2em] opacity-50">Est. 2026</span>
                        <div className="text-xs uppercase tracking-[0.2em] space-x-8 hidden md:block">
                            <span>Indonesia</span>
                            <span>Digital Art</span>
                        </div>
                    </section>

                    <div className="px-2 md:px-6 mb-20">
                        <h1 className="font-sans font-black text-[15vw] leading-[0.8] tracking-tighter uppercase text-center md:text-left">
                            <RevealText>The code</RevealText>
                            <span className="block text-right opacity-50">& the craft</span>
                        </h1>
                    </div>

                    {/* IMAGE / BANNER - BENTO STYLE */}
                    <section className="px-4 md:px-8 mb-32">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <motion.div
                                initial={{ clipPath: "inset(0 100% 0 0)" }}
                                whileInView={{ clipPath: "inset(0 0% 0 0)" }}
                                viewport={{ once: true }}
                                transition={{ duration: 1.5, ease: [0.76, 0, 0.24, 1] }}
                                className="md:col-span-8 aspect-video bg-[#111] overflow-hidden rounded-sm border border-white/10"
                            >
                                <img
                                    src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=2070&auto=format&fit=crop"
                                    alt="My workspace"
                                    className="w-full h-full object-cover opacity-60 hover:opacity-100 transition-opacity duration-700 hover:scale-105 transform"
                                />
                            </motion.div>
                            <div className="md:col-span-4 flex flex-col justify-end p-6 md:p-8 bg-[#111] rounded-sm border border-white/10">
                                <h3 className="text-xs uppercase tracking-[0.2em] opacity-50 mb-6">Manifesto</h3>
                                <p className="text-lg leading-relaxed opacity-80">
                                    I build by hand. Control over performance. Control over pixels. Control over the microscopic details that define a premium experience.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* PHILOSOPHY GRID */}
                    <section className="px-4 md:px-8 mb-32">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {PHILOSOPHY.map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                    className="border border-white/10 p-8 rounded-sm hover:bg-white/5 transition-colors duration-500"
                                >
                                    <h3 className="font-migra text-3xl mb-6 italic">{item.title}</h3>
                                    <p className="text-sm opacity-60 leading-relaxed">{item.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </section>

                    {/* STATS - NOIR STYLE */}
                    <section className="px-4 md:px-8 mb-32">
                        <div className="border border-white/10 rounded-sm p-8 md:p-12">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
                                {[
                                    { label: "Experience", value: "05+" },
                                    { label: "Shipped", value: "40+" },
                                    { label: "Countries", value: "12" },
                                    { label: "Lines Code", value: "1M+" }
                                ].map((stat, i) => (
                                    <div key={i} className="text-center">
                                        <motion.div
                                            initial={{ scale: 0.5, opacity: 0 }}
                                            whileInView={{ scale: 1, opacity: 1 }}
                                            viewport={{ once: true }}
                                            className="font-sans font-black text-6xl md:text-8xl mb-2"
                                        >
                                            {stat.value}
                                        </motion.div>
                                        <div className="text-xs uppercase tracking-[0.2em] opacity-50">{stat.label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    <div className="h-[20vh]"></div>

                    <Footer />
                </main>

                <VideoModal
                    isOpen={videoModalOpen}
                    onClose={() => setVideoModalOpen(false)}
                />
            </div>
        </PageTransition>
    );
};

export default About;
